import { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../services/firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { Eye, EyeOff, LogIn, UserX } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(() => {
    // Initialize based on previously stored preference
    return localStorage.getItem('authPersistence') === 'local';
  });
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // No longer checking for account deactivation on page load
  // Only check during login attempts in handleLogin

  // Track when user is logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        localStorage.setItem('wasLoggedIn', 'true');
      } else {
        localStorage.removeItem('wasLoggedIn');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Store auth persistence preference in localStorage
      localStorage.setItem('authPersistence', staySignedIn ? 'local' : 'session');

      // First, attempt Firebase authentication
      await signInWithEmailAndPassword(auth, email, password);

      // Get user document using the authenticated user's UID
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));

      if (!userDoc.exists()) {
        await auth.signOut();
        setError("Your account is not registered in the system.");
        setIsLoading(false);
        return;
      }

      const userData = userDoc.data();

      if (userData.isActive === false || userData.isActive === "false") {
        await auth.signOut();
        setErrorTitle("Account Disabled");
        setErrorMessage("Your account has been disabled by the admin.");
        setShowErrorModal(true);
        setIsLoading(false);
        return;
      }

      // Navigate based on role
      const role = userData.role || "student";

      if (role === "admin") {
        navigate("/admin");
      } else if (role === "technician") {
        navigate("/technician");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      if (err.code === 'auth/user-disabled') {
        setErrorTitle("Account Disabled");
        setErrorMessage("Your account has been disabled by the admin.");
        setShowErrorModal(true);
      } else {
        setError("Invalid email or password.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat animate-fade-in"
      style={{
        backgroundImage: `url('/cathsbgfinal.png')`,
      }}
    >
      {/* Enhanced gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900/60 via-primary-900/40 to-neutral-900/60"></div>

      {/* Glass morphism login form */}
      <div className="relative z-10 w-full max-w-sm glass-card p-6 mx-4 animate-scale-in">
        {/* Logo/Brand Section */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <LogIn className="w-6 h-6 text-primary-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">
            Welcome Back
          </h1>
          <p className="text-neutral-300 text-xs">
            Sign in to the CATHS Asset Management System
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="form-label text-white">Email Address</label>
            <input
              type="email"
              placeholder="Enter your email"
              className="form-input bg-surface-200/50 border-surface-300/50 text-white placeholder-neutral-400 focus:border-primary-400 focus:ring-primary-400"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="form-label text-white">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="form-input bg-surface-200/50 border-surface-300/50 text-white placeholder-neutral-400 focus:border-primary-400 focus:ring-primary-400 pr-12"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-white transition-colors duration-200"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Stay Signed In Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="staySignedIn"
              checked={staySignedIn}
              onChange={(e) => setStaySignedIn(e.target.checked)}
              className="h-4 w-4 text-primary-400 bg-surface-200/50 border-surface-300/50 rounded focus:ring-primary-400 focus:ring-offset-0"
            />
            <label htmlFor="staySignedIn" className="ml-2 text-sm text-neutral-300">
              Stay signed in
            </label>
          </div>

          {error && (
            <div className="bg-error-500/20 border border-error-500/30 rounded-lg p-3">
              <p className="text-white text-sm text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Signing In...</span>
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/forgot-password')}
            className="text-primary-300 hover:text-primary-200 text-xs transition-colors duration-200 hover:underline"
          >
            Forgot your password?
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-surface-300/30">
          <p className="text-center text-xs text-white">
            © 2025 CATHS Asset Management System
            <br />
            All rights reserved.
          </p>
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          {/* Glass morphism card */}
          <div className="relative z-10 max-w-md w-full glass-card p-8 mx-4 animate-scale-in text-center">
            {/* Logo/Brand Section */}
            <div className="mb-6">
              <img
                src="/cathslogonobg.png"
                alt="CATHS Logo"
                className="w-20 h-20 mx-auto mb-4 filter brightness-0 invert"
              />
              <div className="w-16 h-16 bg-error-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserX className="w-8 h-8 text-error-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{errorTitle}</h2>
              <p className="text-neutral-300 text-sm">{errorMessage}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary w-full py-3 text-base font-semibold"
            >
              Go Back to Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
